<template>
  <Modal
    v-model="isVisible"
    title="客户画像智能分析"
    width="90%"
    :closable="false"
    :mask-closable="false"
    footer-hide
    class="ai-report-modal"
  >
    <div slot="header" class="info-header">
      <a class="ivu-modal-close" @click="closeModal"><i class="ivu-icon ivu-icon-ios-close"></i></a>
    </div>
    <div class="ai-analysis-container">
      <!-- 头部 -->
      <Card :bordered="false" class="header-card">
        <div class="header-content">
          <div class="title-section">
            <h1>客户画像智能分析报告</h1>
            <div class="tag-group">
              <Tag color="primary" type="dot">AI生成</Tag>
              <Tag color="green">Markdown格式</Tag>
              <Tag color="orange">实时分析</Tag>
            </div>
          </div>
          <Alert v-if="errorMsg" type="error" show-icon>{{ errorMsg }}</Alert>
        </div>
        <div class="report-meta">
          <div class="meta-item">
            <Icon type="ios-person" />
            <span class="label">客户姓名:</span>
            <span class="value">{{name }}</span>
          </div>
          <div class="meta-item">
            <Icon type="ios-time" />
            <span class="label">生成时间:</span>
            <span class="value">{{ currentTime }}</span>
          </div>
          <div class="meta-item">
            <Icon type="ios-stats" />
            <span class="label">分析进度:</span>
            <span class="value">{{ progressLabel }}</span>
          </div>
        </div>
      </Card>

      <!-- 控制区域 -->
      <Card class="control-card">
        <Form :label-width="80" inline class="search-form">
          <FormItem label="客户姓名" class="customer-id-input">
            <Input v-model="name" placeholder="请输入客户身份账号" readonly>
              <Icon type="ios-person" slot="prefix" />
            </Input>
          </FormItem>
          <FormItem label="身份证号" class="customer-id-input">
            <Input v-model="cardNo" placeholder="请输入客户身份账号" readonly>
              <Icon type="ios-person" slot="prefix" />
            </Input>
          </FormItem>
          <FormItem>
            <Button type="primary" :loading="loading" @click="startAnalysis">
              <Icon type="md-play-circle" />
              {{ loading ? '分析中...' : '重新分析' }}
            </Button>
          </FormItem>
        </Form>
      </Card>

      <!-- 分析结果区域 -->
      <div class="analysis-sections">
        <!-- 支出分析部分 -->
        <Card class="analysis-section" :bordered="false">
          <div class="section-header">
            <div class="section-title">
              <Icon type="md-cash" size="24" color="#ff5722" />
              <h3>支出行为分析</h3>
            </div>
            <div class="section-actions">
              <Tooltip content="复制内容" placement="top">
                <Button size="small" @click="copyContent('expense')">
                  <Icon type="md-copy" />
                </Button>
              </Tooltip>
              <Tooltip content="折叠/展开" placement="top">
                <Button size="small" @click="toggleSection('expense')">
                  <Icon :type="expenseCollapsed ? 'md-arrow-dropdown' : 'md-arrow-dropup'" />
                </Button>
              </Tooltip>
            </div>
          </div>
          <CollapseTransition>
            <div v-show="!expenseCollapsed" class="analysis-content" ref="expenseContent">
              <div v-if="!expenseAnalysis" class="empty-placeholder">
                <Icon type="ios-document" size="48" />
                <p>等待支出分析结果...</p>
              </div>
              <div v-else class="markdown-renderer">
                <VueMarkdown
                  :key="expenseRenderKey"
                  :source="processLineBreaks(expenseAnalysis)"
                  class="markdown-body"
                />
              </div>
            </div>
          </CollapseTransition>
        </Card>

        <!-- 收入分析部分 -->
        <Card class="analysis-section" :bordered="false">
          <div class="section-header">
            <div class="section-title">
              <Icon type="md-wallet" size="24" color="#4caf50" />
              <h3>收入来源分析</h3>
            </div>
            <div class="section-actions">
              <Tooltip content="复制内容" placement="top">
                <Button size="small" @click="copyContent('income')">
                  <Icon type="md-copy" />
                </Button>
              </Tooltip>
              <Tooltip content="折叠/展开" placement="top">
                <Button size="small" @click="toggleSection('income')">
                  <Icon :type="incomeCollapsed ? 'md-arrow-dropdown' : 'md-arrow-dropup'" />
                </Button>
              </Tooltip>
            </div>
          </div>
          <CollapseTransition>
            <div v-show="!incomeCollapsed" class="analysis-content" ref="incomeContent">
              <div v-if="!incomeAnalysis" class="empty-placeholder">
                <Icon type="ios-document" size="48" />
                <p>等待收入分析结果...</p>
              </div>
              <div v-else class="markdown-renderer">
                <VueMarkdown
                  :key="incomeRenderKey"
                  :source="processLineBreaks(incomeAnalysis)"
                  class="markdown-body"
                />
              </div>
            </div>
          </CollapseTransition>
        </Card>
      </div>

      <!-- 状态信息 -->
      <Card class="status-card" v-if="statusMessage">
        <div class="status-content">
          <Icon type="ios-information-circle" size="20" color="#1890ff" />
          <span>{{ statusMessage }}</span>
        </div>
      </Card>

      <!-- 底部操作按钮 -->
      <div class="footer-actions">
        <Button type="primary" @click="exportReport" :disabled="loading">
          <Icon type="md-download" />
          导出PDF报告
        </Button>
        <Button type="default" @click="closeModal">关闭</Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import { getToken } from '@/libs/util'
import config from '@/config/index.js'
import VueMarkdown from 'vue-markdown'
import { CollapseTransition } from 'iview'
import axios from 'axios'
export default {
  name: 'AiReport',
  components: { VueMarkdown, CollapseTransition },
  data () {
    return {
      isVisible: false,
      customId: '',
      cardNo: '',
      name: '',
      loading: false,
      progress: 0,
      progressStatus: 'active',
      errorMsg: '',
      statusMessage: '',
      // 原始内容缓冲区
      expenseBuffer: '',
      incomeBuffer: '',
      // 用于渲染的内容
      expenseAnalysis: '',
      incomeAnalysis: '',
      eventSource: null,
      expenseCollapsed: false,
      incomeCollapsed: false,
      currentTime: new Date().toLocaleString(),
      // 节流计时器
      renderTimer: null,
      // 渲染间隔(毫秒)
      renderInterval: 200,
      // 用于强制重新渲染的key
      expenseRenderKey: 0,
      incomeRenderKey: 0,
      // 标记是否完成流式传输
      isExpenseComplete: false,
      isIncomeComplete: false
    }
  },
  computed: {
    progressLabel () {
      if (this.progress === 100) return '分析完成'
      if (this.progressStatus === 'wrong') return '分析失败'
      return `分析中 ${this.progress}%`
    }
  },
  methods: {
    /**
     * 处理换行符，将转义字符转换为实际换行
     */
    processLineBreaks (content) {
      if (!content) return ''
      // 将转义的换行符替换为实际换行符
      return content
        .replace(/\\n\\n/g, '\n\n') // 处理双换行
        .replace(/\\n/g, '\n') // 处理单换行
        .replace(/<br\s*\/?>/gi, '\n') // 处理HTML换行标签
    },

    openModel (type, param) {
      this.resetAnalysis()
      this.currentTime = new Date().toLocaleString()

      if (param && param.id) {
        this.customId = param.id
        this.cardNo = param.cardNo
        this.name = param.name
        this.isVisible = true
        // 延迟开始分析，确保DOM渲染完成
        setTimeout(() => {
          this.startAnalysis()
        }, 300)
      } else {
        this.$Message.error('缺少客户ID参数')
      }
    },

    openModelOne (type, data) {
      if (data) {
        this.resetAnalysis()
        this.currentTime = new Date().toLocaleString()
        this.customId = data
        this.isVisible = true
        // 延迟开始分析，确保DOM渲染完成
        setTimeout(() => {
          this.startAnalysis()
        }, 300)
      } else {
        this.$Message.error('缺少客户ID参数')
      }
    },

    startAnalysis: function () {
      if (!this.customId) {
        this.$Message.error('请输入客户ID')
        return
      }

      this.resetAnalysis()
      this.loading = true
      this.progress = 10
      this.statusMessage = '连接分析服务...'

      const isProduction = process.env.NODE_ENV === 'production'
      const baseUrl = isProduction ? config.baseUrl.pro : config.baseUrl.dev
      const baseDomain = baseUrl.replace('/api/', '')

      const fullUrl = `${baseDomain}/api/custominfo/portrait/${this.customId}?token=${getToken()}`
      console.log('SSE连接URL:', fullUrl)

      // 建立SSE连接
      this.eventSource = new EventSource(fullUrl)

      // 事件处理 - 支出分析（带缓冲）
      this.eventSource.addEventListener('expense', (event) => {
        this.handleStreamData('expense', event.data)
      })

      // 支出分析完成事件
      this.eventSource.addEventListener('expense_complete', () => {
        this.isExpenseComplete = true
        this.flushBuffers('expense')
        this.expenseRenderKey += 1
      })

      // 事件处理 - 收入分析（带缓冲）
      this.eventSource.addEventListener('income', (event) => {
        this.handleStreamData('income', event.data)
      })

      // 收入分析完成事件
      this.eventSource.addEventListener('income_complete', () => {
        this.isIncomeComplete = true
        this.flushBuffers('income')
        this.incomeRenderKey += 1
      })

      // 状态消息处理
      this.eventSource.addEventListener('status', (event) => {
        this.statusMessage = event.data
      })

      // 进度值处理
      this.eventSource.addEventListener('progress', (event) => {
        this.progress = parseInt(event.data) || 0
      })

      // 完成事件
      this.eventSource.addEventListener('complete', async (event) => {
        this.progress = 100
        this.progressStatus = 'success'
        this.statusMessage = event.data || '分析完成'
        if (event.data && (event.data.includes('使用缓存数据') || event.data.trim() === '使用缓存数据')) {
          this.$Message.success('已加载缓存分析结果')
          this.flushAllBuffers()
          this.isExpenseComplete = true
          this.isIncomeComplete = true
          this.expenseRenderKey += 1
          this.incomeRenderKey += 1
          this.endAnalysis()
          return // 直接结束，不执行后续接口调用
        }

        // 非缓存场景（实时流式生成完成），调用接口获取完整数据优化格式
        try {
          this.loading = true
          this.statusMessage = '正在优化格式...'
          const response = await axios.get(`${baseDomain}/api/custominfo/getCompleteAnalysis/${this.customId}?token=${getToken()}`)
          console.log('获取完整分析结果:', response.data)
          if (response.data.success) {
            const { expense, income } = response.data.data
            this.expenseAnalysis = this.processLineBreaks(expense)
            this.incomeAnalysis = this.processLineBreaks(income)
            this.expenseRenderKey += 1
            this.incomeRenderKey += 1
          } else {
            console.log('获取完整分析结果:', 'shibai')
          }
        } catch (err) {
          console.error('获取完整分析结果失败', err)
          this.$Message.warning('格式优化失败，使用原始分析结果')
        } finally {
          this.loading = false
          this.statusMessage = '分析完成'
          this.$Message.success('分析完成')
          this.endAnalysis()
        }
      })

      // 错误事件
      this.eventSource.addEventListener('error', (event) => {
        this.errorMsg = event.data
        this.progressStatus = 'wrong'
        /*     this.statusMessage = '分析发生错误'
        this.$Message.error(`分析失败: ${event.data}`) */
        this.endAnalysis()
      })

      // 连接错误处理
      this.eventSource.onerror = (err) => {
        console.error('SSE连接错误:', err)
        /*   this.$Message.error('分析连接失败，请重试') */
        this.endAnalysis()
      }
    },

    /**
     * 处理流式数据，管理缓冲区
     */
    handleStreamData (type, data) {
      // 处理转义的换行符
      let processedData = this.processLineBreaks(data)

      // 将数据添加到缓冲区
      if (type === 'expense') {
        this.expenseBuffer += processedData
      } else {
        this.incomeBuffer += processedData
      }

      // 清除现有计时器
      if (this.renderTimer) {
        clearTimeout(this.renderTimer)
      }

      // 设置新计时器，延迟渲染以累积足够数据
      this.renderTimer = setTimeout(() => {
        this.flushBuffers(type)
        // 触发重新渲染
        if (type === 'expense') {
          this.expenseRenderKey += 1
        } else {
          this.incomeRenderKey += 1
        }
      }, this.renderInterval)
    },

    /**
     * 刷新指定类型的缓冲区到渲染区
     */
    flushBuffers (type) {
      if (type === 'expense' && this.expenseBuffer) {
        this.expenseAnalysis += this.expenseBuffer
        this.expenseBuffer = ''
      } else if (type === 'income' && this.incomeBuffer) {
        this.incomeAnalysis += this.incomeBuffer
        this.incomeBuffer = ''
      }
    },

    /**
     * 刷新所有缓冲区
     */
    flushAllBuffers () {
      this.flushBuffers('expense')
      this.flushBuffers('income')
    },

    resetAnalysis () {
      this.expenseBuffer = ''
      this.incomeBuffer = ''
      this.expenseAnalysis = ''
      this.incomeAnalysis = ''
      this.errorMsg = ''
      this.progress = 0
      this.progressStatus = 'active'
      this.statusMessage = ''
      this.expenseCollapsed = false
      this.incomeCollapsed = false
      this.isExpenseComplete = false
      this.isIncomeComplete = false
      this.expenseRenderKey = 0
      this.incomeRenderKey = 0

      if (this.renderTimer) {
        clearTimeout(this.renderTimer)
        this.renderTimer = null
      }

      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }
    },

    endAnalysis () {
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }
      this.loading = false

      if (this.renderTimer) {
        clearTimeout(this.renderTimer)
        this.renderTimer = null
      }
    },

    closeModal () {
      this.resetAnalysis()
      this.isVisible = false
    },

    copyContent (type) {
      // 复制前处理换行符
      const content = type === 'expense' ? this.processLineBreaks(this.expenseAnalysis) : this.processLineBreaks(this.incomeAnalysis)
      navigator.clipboard.writeText(content).then(() => {
        this.$Message.success('内容已复制到剪贴板')
      }).catch(err => {
        console.error('复制失败:', err)
        this.$Message.error('复制失败')
      })
    },

    toggleSection (type) {
      if (type === 'expense') {
        this.expenseCollapsed = !this.expenseCollapsed
      } else {
        this.incomeCollapsed = !this.incomeCollapsed
      }
    },

    exportReport () {
      // 1. 处理分析数据
      const processedExpense = this.processLineBreaks(this.expenseAnalysis) || '无支出分析数据'
      const processedIncome = this.processLineBreaks(this.incomeAnalysis) || '无收入分析数据'

      // 2. 创建临时打印容器（关键：固定宽度=A4纸宽度-边距，避免过宽分页）
      const printContainer = document.createElement('div')
      printContainer.id = 'temp-print-container'
      printContainer.style.cssText = `
      font-family: Arial, "Microsoft YaHei", sans-serif;
      width: 210mm; /* A4纸宽度（210mm），刚好匹配打印尺寸 */
      margin: 0 auto; /* 居中 */
      padding: 15px 0; /* 仅上下留白，左右贴边 */
      line-height: 1.6; /* 压缩行高，避免内容过散 */
      color: #333; /* 确保文字清晰 */
    `

      // 3. 填充内容（优化间距+分页控制）
      printContainer.innerHTML = `
      <!-- 报告标题（紧凑间距，避免单独占页） -->
      <div style="text-align: center; margin: 10px 0 30px; page-break-after: avoid;">
        <h1 style="font-size: 20px; margin: 0 0 15px; font-weight: bold;">客户画像智能分析报告</h1>
        <!-- 客户信息紧跟标题，同页显示 -->
        <div style="font-size: 14px; text-align: left; padding: 0 10px;">
          <p style="margin: 5px 0;">客户姓名：${this.name || '未知'}</p>
          <p style="margin: 5px 0;">身份证号：${this.cardNo || '未知'}</p>
          <p style="margin: 5px 0;">生成时间：${this.currentTime}</p>
        </div>
      </div>

      <!-- 支出行为分析（标题+内容同页，禁止截断） -->
      <div style="margin: 20px 10px; page-break-inside: avoid; page-break-after: avoid;">
        <h2 style="font-size: 16px; margin: 0 0 12px; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 5px;">
          支出行为分析
        </h2>
        <div style="font-size: 14px; margin: 0; white-space: pre-wrap;">
          ${processedExpense}
        </div>
      </div>

      <!-- 收入来源分析（与支出分析紧凑衔接，避免空页） -->
      <div style="margin: 25px 10px; page-break-inside: avoid;">
        <h2 style="font-size: 16px; margin: 0 0 12px; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 5px;">
          收入来源分析
        </h2>
        <div style="font-size: 14px; margin: 0; white-space: pre-wrap;">
          ${processedIncome}
        </div>
      </div>

      <!-- 页脚（内容结束后显示，避免单独占页） -->
      <div style="margin: 30px 10px 0; text-align: center; font-size: 12px; color: #666; page-break-before: avoid;">
        本报告由AI智能分析生成，仅供参考 | 生成时间：${this.currentTime}
      </div>
    `

      // 4. 打印专用样式（核心：控制分页+边距）
      const printStyle = document.createElement('style')
      printStyle.textContent = `
      /* 打印时隐藏页面其他内容 */
      @media print {
        body > *:not(#temp-print-container):not(#temp-print-container *) {
          display: none !important;
        }
        /* 固定打印容器位置，匹配A4纸 */
        #temp-print-container {
          position: absolute !important;
          top: 0 !important;
          left: 30% !important;
          transform: translateX(-30%) !important; /* 精准居中 */
          width: 210mm !important; /* 强制A4宽度 */
        }
        /* 优化打印边距（1cm=默认最小边距，容纳更多内容） */
        @page {
          margin: 1cm;
          size: A4 portrait; /* 纵向A4 */
          marks: none; /* 隐藏打印标记 */
        }
        /* 禁止关键内容截断 */
        h1, h2, div {
          page-break-inside: avoid; /* 内容块内不分页 */
        }
        h2 {
          page-break-after: avoid; /* 标题后不单独分页 */
        }
        /* 去除链接下划线等冗余样式 */
        a {
          text-decoration: none;
          color: #333;
        }
      }
    `

      // 5. 添加到页面并触发打印
      document.body.appendChild(printStyle)
      document.body.appendChild(printContainer)

      setTimeout(() => {
        window.print()
        // 打印后清理
        setTimeout(() => {
          document.body.removeChild(printContainer)
          document.body.removeChild(printStyle)
        }, 500)
      }, 100)

      this.$Message.info('请在打印预览中选择「另存为PDF」完成导出')
    }
  }
}
</script>

<style scoped>
.ai-analysis-container {
  max-width: 1600px;
  margin: 20px auto;
  padding: 20px;
  background-color: #f5f7fa;
}

.header-card {
  margin-bottom: 20px;
  background: linear-gradient(135deg, #1890ff 0%, #0050b3 100%);
  color: white;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.title-section h1 {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
}

.tag-group {
  display: flex;
  gap: 8px;
}

.report-meta {
  display: flex;
  gap: 20px;
  margin-top: 15px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 15px;
  border-radius: 4px;
  gap: 8px;
}

.meta-item .label {
  font-weight: bold;
}

.control-card {
  margin-bottom: 20px;
  padding: 20px;
}

.search-form {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.customer-id-input {
  flex-grow: 1;
  margin-right: 10px;
}

.reset-btn {
  margin-left: 10px;
}

.progress-bar {
  margin-top: 20px;
  position: relative;
}

.progress-icon-spin {
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.analysis-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.analysis-section {
  padding: 20px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #2c3e50;
}

.section-actions {
  display: flex;
  gap: 5px;
}

.analysis-content {
  position: relative;
  overflow: auto;
  transition: all 0.3s ease;
  background: white;
  border-radius: 6px;
  padding: 15px;
  max-height: 600px;
}

.empty-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;
}

.status-card {
  padding: 15px 20px;
}

.status-content {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #1890ff;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.markdown-renderer {
  width: 100%;
}

@media (max-width: 1200px) {
  .analysis-sections {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .ai-analysis-container {
    padding: 10px;
  }

  .search-form {
    flex-direction: column;
    align-items: flex-start;
  }

  .customer-id-input {
    width: 100%;
    margin-right: 0;
    margin-bottom: 15px;
  }

  .report-meta {
    flex-direction: column;
    gap: 10px;
  }

  .header-content {
    flex-direction: column;
    gap: 15px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .section-actions {
    align-self: flex-end;
  }
}

/* Markdown渲染样式 */
.markdown-body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.8;
  word-wrap: break-word;
  color: #2c3e50;
}

/* 标题样式 */
.markdown-body h1 {
  font-size: 1.8em !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  border-bottom: 3px solid #3498db !important;
  padding-bottom: 0.5em !important;
  margin-top: 2em !important;
  margin-bottom: 1.5em !important;
}

.markdown-body h2 {
  font-size: 1.5em !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
  margin-top: 1.8em !important;
  margin-bottom: 1.2em !important;
  padding-left: 10px !important;
  border-left: 4px solid #3498db !important;
}

.markdown-body h3 {
  font-size: 1.3em !important;
  font-weight: 600 !important;
  color: #34495e !important;
  margin-top: 1.6em !important;
  margin-bottom: 1em !important;
}

/* 段落和文本样式 */
.markdown-body p {
  margin-bottom: 1.5em !important;
  line-height: 1.8 !important;
}

/* 列表样式 */
.markdown-body ul,
.markdown-body ol {
  padding-left: 2.2em !important;
  margin-bottom: 1.5em !important;
}

.markdown-body li {
  margin-bottom: 0.8em !important;
}

/* 表格样式 */
.markdown-body table {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 2em 0 !important;
}

.markdown-body table th,
.markdown-body table td {
  padding: 12px 15px !important;
  border: 1px solid #dfe2e5 !important;
  text-align: left !important;
}

.markdown-body table th {
  background-color: #f6f8fa !important;
  font-weight: 600 !important;
}

/* 代码块样式 */
.markdown-body pre {
  margin: 2em 0 !important;
  padding: 1.5em !important;
  background-color: #f8f9fa !important;
  border-radius: 6px !important;
  overflow-x: auto !important;
}

/* 引用块样式 */
.markdown-body blockquote {
  margin: 2em 0 !important;
  padding: 1em 1.5em !important;
  border-left: 4px solid #3498db !important;
  background-color: #f8fafc !important;
}
</style>
